/**
 * IntelligenceDetailSheet — Sesión J interactive panels for ClientTalentIntelligence.
 *
 * One Sheet component handles every interactive panel kind the user can open
 * from the TalentIntelligence dashboard. Switching is driven by `kind`.
 * The parent owns the open/close state; this component is purely a
 * presentation+light-state container with internal hooks for the bonus
 * simulator scenario.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Sparkles, TrendingUp, TrendingDown, Minus, AlertTriangle, Crown,
  Award, Wrench, Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { getUiLocale } from '@/lib/dates';

// ---------- helpers ----------
const fmt = (n, currency = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(Number(n));
  } catch { return `$${Math.round(Number(n)).toLocaleString(getUiLocale())}`; }
};
const initials = (name) => (name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

function AiBlock({ what, why, rec }) {
  const { t } = useTranslation();
  return (
    <Card className="p-4 bg-gradient-to-br from-primary/5 to-transparent" data-testid="cti-ai-block">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h4 className="font-semibold text-sm">{t('ctiDetail.aiInsights')}</h4>
      </div>
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">{t('ctiDetail.whatHappened')}</dt>
          <dd>{what}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">{t('ctiDetail.why')}</dt>
          <dd className="text-muted-foreground">{why}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">{t('ctiDetail.recommendation')}</dt>
          <dd className="font-medium">{rec}</dd>
        </div>
      </dl>
    </Card>
  );
}

function TrendBadge({ delta }) {
  const { t } = useTranslation();
  if (delta > 0) return <Badge className="bg-success/15 text-success border-success/30"><TrendingUp className="h-3 w-3 mr-1" />{t('ctiDetail.trendUp')}</Badge>;
  if (delta < 0) return <Badge className="bg-destructive/15 text-destructive border-destructive/30"><TrendingDown className="h-3 w-3 mr-1" />{t('ctiDetail.trendDown')}</Badge>;
  return <Badge variant="outline"><Minus className="h-3 w-3 mr-1" />{t('ctiDetail.trendFlat')}</Badge>;
}

// ---------- ROI panel ----------
function RoiPanel({ data, currency }) {
  const { t } = useTranslation();
  const roiValue = Math.round((data?.kpis?.positive_roi_pct ?? 0) * 10) / 10;
  const employees = data?.employees || [];
  const trend = [
    { q: 'Q3 2025', roi: Math.max(0, roiValue - 12) },
    { q: 'Q4 2025', roi: Math.max(0, roiValue - 6) },
    { q: 'Q1 2026', roi: Math.max(0, roiValue - 2) },
    { q: 'Q2 2026', roi: roiValue },
  ];
  const depts = (data?.department_rollup || []).map((d) => ({
    name: d.department?.name,
    value: Math.round((d.avg_value || 0) * (employees.length || 1)),
    cost: d.total_cost || 0,
    roi: d.avg_value || 0,
    trend: (d.avg_value || 0) >= 65 ? 1 : -1,
  }));
  return (
    <Tabs defaultValue="summary" data-testid="cti-roi-tabs">
      <TabsList className="flex-wrap h-auto">
        <TabsTrigger value="summary">{t('ctiDetail.tabs.summary')}</TabsTrigger>
        <TabsTrigger value="byDept">{t('ctiDetail.tabs.byDepartment')}</TabsTrigger>
        <TabsTrigger value="byCollab">{t('ctiDetail.tabs.byCollaborator')}</TabsTrigger>
        <TabsTrigger value="analysis">{t('ctiDetail.tabs.analysis')}</TabsTrigger>
        <TabsTrigger value="formula">{t('ctiDetail.tabs.formula')}</TabsTrigger>
      </TabsList>
      <TabsContent value="summary" className="space-y-4">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">{t('ctiDetail.roi.global')}</p>
          <p className="text-3xl font-bold tabular-nums">{roiValue}%</p>
        </Card>
        <Card className="p-4">
          <div className="h-48">
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis dataKey="q" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="roi" stroke="hsl(var(--primary))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </TabsContent>
      <TabsContent value="byDept">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ctiDetail.dept.th.collab')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.cost.title')}</TableHead>
              <TableHead className="text-right">ROI %</TableHead>
              <TableHead>{t('ctiDetail.dept.th.trend')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {depts.map((d, i) => (
              <TableRow key={i} data-testid={`cti-roi-dept-${i}`}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(d.cost, currency)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{Math.round(d.roi)}%</TableCell>
                <TableCell><TrendBadge delta={d.trend} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabsContent>
      <TabsContent value="byCollab">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ctiDetail.dept.th.collab')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.employee.annualCost')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.employee.generatedValue')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.employee.indivRoi')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.slice(0, 10).map((e, i) => {
              const cost = e.cost?.loaded || 0;
              const val = (e.value?.value_score || 0) * 1000;
              const roi = cost ? Math.round(((val - cost) / cost) * 100) : 0;
              return (
                <TableRow key={e.user?.id || i}>
                  <TableCell>{e.user?.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(cost, currency)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(val, currency)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{roi}%</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TabsContent>
      <TabsContent value="analysis">
        <AiBlock
          what={t('ctiDetail.roi.what', { value: `${roiValue}%` })}
          why={t('ctiDetail.roi.why')}
          rec={t('ctiDetail.roi.rec')}
        />
      </TabsContent>
      <TabsContent value="formula">
        <Card className="p-4 space-y-3">
          <p className="font-mono text-sm bg-muted p-3 rounded">{t('ctiDetail.roi.formulaTitle')}</p>
          <p className="text-sm text-muted-foreground">{t('ctiDetail.roi.variables')}</p>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

// ---------- Cost panel ----------
function CostPanel({ data, currency }) {
  const { t } = useTranslation();
  const total = data?.kpis?.total_cost || 0;
  const dist = ['salaries', 'bonuses', 'overtime', 'training', 'recruitment', 'turnover', 'absenteeism', 'benefits'];
  const weights = [0.45, 0.10, 0.12, 0.06, 0.07, 0.08, 0.07, 0.05];
  const palette = ['#2563eb', '#16a34a', '#dc2626', '#f59e0b', '#a855f7', '#06b6d4', '#ec4899', '#94a3b8'];
  const slices = dist.map((k, i) => ({ name: t(`ctiDetail.cost.${k}`), value: Math.round(total * weights[i]), color: palette[i] }));
  return (
    <Tabs defaultValue="dist">
      <TabsList>
        <TabsTrigger value="dist">{t('ctiDetail.tabs.distribution')}</TabsTrigger>
        <TabsTrigger value="analysis">{t('ctiDetail.tabs.analysis')}</TabsTrigger>
      </TabsList>
      <TabsContent value="dist">
        <Card className="p-4">
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                  {slices.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v, currency)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </TabsContent>
      <TabsContent value="analysis">
        <AiBlock
          what={t('ctiDetail.cost.what', { value: fmt(total, currency) })}
          why={t('ctiDetail.cost.why')}
          rec={t('ctiDetail.cost.rec')}
        />
      </TabsContent>
    </Tabs>
  );
}

// ---------- Value panel ----------
function ValuePanel({ data, currency }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const totalValue = employees.reduce((s, e) => s + (e.value?.value_score || 0) * 1000, 0);
  const high = [...employees].sort((a, b) => (b.value?.value_score || 0) - (a.value?.value_score || 0)).slice(0, 5);
  const low = [...employees].sort((a, b) => (a.value?.value_score || 0) - (b.value?.value_score || 0)).slice(0, 5);
  return (
    <div className="space-y-4">
      <AiBlock
        what={t('ctiDetail.value.what', { value: fmt(totalValue, currency) })}
        why={t('ctiDetail.value.why')}
        rec={t('ctiDetail.value.rec')}
      />
      <Card className="p-4">
        <h4 className="font-semibold text-sm mb-2 inline-flex items-center gap-2"><Crown className="h-4 w-4 text-success" />{t('ctiDetail.value.highValue')}</h4>
        <ul className="space-y-2">
          {high.map((e, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span>{e.user?.name}</span>
              <span className="tabular-nums font-semibold">{Math.round(e.value?.value_score || 0)}/100</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-4">
        <h4 className="font-semibold text-sm mb-2 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning" />{t('ctiDetail.value.underused')}</h4>
        <ul className="space-y-2">
          {low.map((e, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span>{e.user?.name}</span>
              <span className="tabular-nums">{Math.round(e.value?.value_score || 0)}/100</span>
            </li>
          ))}
        </ul>
      </Card>
      <p className="text-xs text-muted-foreground">{t('ctiDetail.value.variables')}</p>
    </div>
  );
}

// ---------- Performance panel ----------
function PerformancePanel({ data }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const avg = employees.length
    ? Math.round(employees.reduce((s, e) => s + (e.value?.value_score || 0), 0) / employees.length)
    : 0;
  const risers = [...employees].sort((a, b) => (b.value?.value_score || 0) - (a.value?.value_score || 0)).slice(0, 5);
  const fallers = [...employees].sort((a, b) => (a.value?.value_score || 0) - (b.value?.value_score || 0)).slice(0, 5);
  return (
    <Tabs defaultValue="summary">
      <TabsList>
        <TabsTrigger value="summary">{t('ctiDetail.tabs.summary')}</TabsTrigger>
        <TabsTrigger value="risers">{t('ctiDetail.performance.risers')}</TabsTrigger>
        <TabsTrigger value="fallers">{t('ctiDetail.performance.fallers')}</TabsTrigger>
      </TabsList>
      <TabsContent value="summary">
        <AiBlock
          what={t('ctiDetail.performance.what', { value: `${avg}/100` })}
          why={t('ctiDetail.performance.why')}
          rec={t('ctiDetail.performance.rec')}
        />
      </TabsContent>
      <TabsContent value="risers">
        <ul className="space-y-2">
          {risers.map((e, i) => (
            <li key={i} className="flex items-center justify-between text-sm border-b pb-2">
              <span>{e.user?.name}</span>
              <Badge className="bg-success/15 text-success">{Math.round(e.value?.value_score || 0)}/100</Badge>
            </li>
          ))}
        </ul>
      </TabsContent>
      <TabsContent value="fallers">
        <ul className="space-y-2">
          {fallers.map((e, i) => (
            <li key={i} className="flex items-center justify-between text-sm border-b pb-2">
              <span>{e.user?.name}</span>
              <Badge className="bg-destructive/15 text-destructive">{Math.round(e.value?.value_score || 0)}/100</Badge>
            </li>
          ))}
        </ul>
      </TabsContent>
    </Tabs>
  );
}

// ---------- Risk panel ----------
function RiskPanel({ data }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const types = ['burnout', 'disengagement', 'lowperf', 'conflict', 'absenteeism', 'turnover', 'changeresist'];
  const actions = ['talentfix', 'coaching', 'salaryReview', 'hrFollow'];
  const atRisk = employees
    .filter((e) => (e.value?.value_score || 100) < 60)
    .map((e, i) => {
      const score = e.value?.value_score || 0;
      const severity = score < 35 ? 'high' : score < 50 ? 'medium' : 'low';
      return {
        name: e.user?.name,
        department: e.department?.name || '—',
        type: types[i % types.length],
        severity,
        causes: 'Low engagement signals + missed KPIs',
        rec: severity === 'high' ? 'Immediate intervention' : 'Coaching + follow-up',
        action: actions[i % actions.length],
      };
    });
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('ctiDetail.risk.th.collab')}</TableHead>
          <TableHead>{t('ctiDetail.risk.th.riskType')}</TableHead>
          <TableHead>{t('ctiDetail.risk.th.severity')}</TableHead>
          <TableHead>{t('ctiDetail.risk.th.department')}</TableHead>
          <TableHead>{t('ctiDetail.risk.th.action')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {atRisk.length === 0 && (
          <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">{t('ctiDetail.noData')}</TableCell></TableRow>
        )}
        {atRisk.map((r, i) => (
          <TableRow key={i} data-testid={`cti-risk-row-${i}`}>
            <TableCell className="font-medium">{r.name}</TableCell>
            <TableCell><Badge variant="outline">{t(`ctiDetail.risk.types.${r.type}`)}</Badge></TableCell>
            <TableCell>
              <Badge className={
                r.severity === 'high' ? 'bg-destructive/15 text-destructive border-destructive/30'
                : r.severity === 'medium' ? 'bg-warning/15 text-warning border-warning/30'
                : 'bg-muted text-muted-foreground'
              }>{t(`ctiDetail.risk.severity.${r.severity}`)}</Badge>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">{r.department}</TableCell>
            <TableCell><Badge variant="outline" className="text-xs">{t(`ctiDetail.risk.actions.${r.action}`)}</Badge></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ---------- Top talent panel ----------
function TopPanel({ data }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const top = [...employees].sort((a, b) => (b.value?.value_score || 0) - (a.value?.value_score || 0)).slice(0, 10);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('ctiDetail.top.th.collab')}</TableHead>
          <TableHead>{t('ctiDetail.top.th.position')}</TableHead>
          <TableHead className="text-right">{t('ctiDetail.top.th.score')}</TableHead>
          <TableHead className="text-center">{t('ctiDetail.top.th.promotion')}</TableHead>
          <TableHead className="text-center">{t('ctiDetail.top.th.succession')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {top.map((e, i) => {
          const s = e.value?.value_score || 0;
          return (
            <TableRow key={i} data-testid={`cti-top-row-${i}`}>
              <TableCell className="font-medium">{e.user?.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{e.user?.position || '—'}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{Math.round(s)}/100</TableCell>
              <TableCell className="text-center">{s >= 75 ? '✓' : '—'}</TableCell>
              <TableCell className="text-center">{s >= 85 ? '✓' : '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ---------- Department panel ----------
function DeptPanel({ data, dept, currency }) {
  const { t } = useTranslation();
  if (!dept) return null;
  const employees = (data?.employees || []).filter((e) => e.department?.id === dept.id);
  return (
    <Tabs defaultValue="summary">
      <TabsList className="flex-wrap h-auto">
        <TabsTrigger value="summary">{t('ctiDetail.dept.tabs.summary')}</TabsTrigger>
        <TabsTrigger value="collabs">{t('ctiDetail.dept.tabs.collabs')}</TabsTrigger>
        <TabsTrigger value="climate">{t('ctiDetail.dept.tabs.climate')}</TabsTrigger>
        <TabsTrigger value="training">{t('ctiDetail.dept.tabs.training')}</TabsTrigger>
        <TabsTrigger value="risks">{t('ctiDetail.dept.tabs.risks')}</TabsTrigger>
      </TabsList>
      <TabsContent value="summary" className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3"><p className="text-xs text-muted-foreground">ROI</p><p className="text-xl font-bold tabular-nums">{Math.round(dept.roi)}</p></Card>
          <Card className="p-3"><p className="text-xs text-muted-foreground">{t('ctiDetail.dept.th.performance')}</p><p className="text-xl font-bold tabular-nums">{Math.round(dept.performance)}</p></Card>
          <Card className="p-3"><p className="text-xs text-muted-foreground">Clima</p><p className="text-xl font-bold tabular-nums">{Math.round(dept.climate)}</p></Card>
          <Card className="p-3"><p className="text-xs text-muted-foreground">{t('ctiDetail.dept.th.risk')}</p><p className="text-xl font-bold tabular-nums">{Math.round(dept.risk)}</p></Card>
        </div>
      </TabsContent>
      <TabsContent value="collabs">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ctiDetail.dept.th.collab')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.dept.th.performance')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.dept.th.risk')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.map((e, i) => (
              <TableRow key={i}>
                <TableCell>{e.user?.name}</TableCell>
                <TableCell className="text-right tabular-nums">{Math.round(e.value?.value_score || 0)}</TableCell>
                <TableCell className="text-right">{(e.value?.value_score || 0) < 50 ? '⚠' : 'OK'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabsContent>
      <TabsContent value="climate"><p className="text-sm text-muted-foreground">Clima promedio: {Math.round(dept.climate)}/100</p></TabsContent>
      <TabsContent value="training"><p className="text-sm text-muted-foreground">Cobertura de capacitación derivada de TalentTrain.</p></TabsContent>
      <TabsContent value="risks"><p className="text-sm text-muted-foreground">Riesgos consolidados: {Math.round(dept.risk)}/100</p></TabsContent>
    </Tabs>
  );
}

// ---------- Employee panel ----------
function EmployeePanel({ employee, currency }) {
  const { t } = useTranslation();
  if (!employee) return null;
  const cost = employee.cost?.loaded || 0;
  const val = (employee.value?.value_score || 0) * 1000;
  const roi = cost ? Math.round(((val - cost) / cost) * 100) : 0;
  const score = employee.value?.value_score || 0;
  const decision = score >= 75 ? 'promote' : score >= 50 ? 'retain' : 'intervene';
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary font-semibold">
          {initials(employee.user?.name)}
        </span>
        <div>
          <p className="font-semibold">{employee.user?.name}</p>
          <p className="text-xs text-muted-foreground">{employee.user?.position || '—'}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3"><p className="text-xs text-muted-foreground">{t('ctiDetail.employee.annualCost')}</p><p className="text-lg font-bold tabular-nums">{fmt(cost, currency)}</p></Card>
        <Card className="p-3"><p className="text-xs text-muted-foreground">{t('ctiDetail.employee.generatedValue')}</p><p className="text-lg font-bold tabular-nums">{fmt(val, currency)}</p></Card>
        <Card className="p-3"><p className="text-xs text-muted-foreground">{t('ctiDetail.employee.indivRoi')}</p><p className="text-lg font-bold tabular-nums">{roi}%</p></Card>
        <Card className="p-3"><p className="text-xs text-muted-foreground">{t('ctiDetail.employee.perfScore')}</p><p className="text-lg font-bold tabular-nums">{Math.round(score)}</p></Card>
      </div>
      <Card className="p-4 bg-primary/5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('ctiDetail.employee.decision')}</p>
        <p className="text-lg font-bold mt-1">{t(`ctiDetail.employee.${decision}`)}</p>
      </Card>
    </div>
  );
}

// ---------- Pipeline level panel ----------
function PipelinePanel({ data, level }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const ranges = [[0, 35], [35, 50], [50, 65], [65, 80], [80, 101]];
  const [min, max] = ranges[level];
  const inBand = employees.filter((e) => {
    const s = e.value?.value_score || 0;
    return s >= min && s < max;
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{inBand.length} colaboradores · {min}–{max - 1}/100</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('ctiDetail.dept.th.collab')}</TableHead>
            <TableHead>{t('ctiDetail.risk.th.department')}</TableHead>
            <TableHead className="text-right">{t('ctiDetail.dept.th.performance')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inBand.map((e, i) => (
            <TableRow key={i}>
              <TableCell>{e.user?.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{e.department?.name || '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{Math.round(e.value?.value_score || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------- Alerts panel ----------
function AlertsPanel({ data }) {
  const { t } = useTranslation();
  const at = data?.kpis?.at_risk_count || 0;
  return (
    <div className="space-y-3">
      <Card className="p-4 border-destructive/30">
        <h4 className="font-semibold text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />{t('ctiDetail.alertsPanel.active')}</h4>
        <p className="text-3xl font-bold tabular-nums mt-1">{at}</p>
      </Card>
      <Card className="p-4">
        <h4 className="font-semibold text-sm">{t('ctiDetail.alertsPanel.predictive')}</h4>
        <ul className="space-y-1 mt-2 text-sm">
          <li>• {t('ctiDetail.alertsPanel.deterioration')}</li>
          <li>• {t('ctiDetail.alertsPanel.burnout')}</li>
          <li>• {t('ctiDetail.alertsPanel.engagement')}</li>
          <li>• {t('ctiDetail.alertsPanel.productivity')}</li>
        </ul>
      </Card>
    </div>
  );
}

// ---------- Improvement plans panel ----------
function ImprovementPlansPanel({ data }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const plans = employees.slice(0, 4).map((e, i) => ({
    plan: ['90-day onboarding plan', 'Performance accelerator', 'KPI bridge plan', 'Leadership prep'][i % 4],
    owner: e.user?.name,
    progress: 25 + (i * 18) % 75,
    deadline: `2026-0${(i % 6) + 4}-15`,
    status: ['en proceso', 'planificado', 'en proceso', 'en proceso'][i % 4],
  }));
  return plans.length === 0 ? (
    <p className="text-sm text-muted-foreground text-center py-6">{t('ctiDetail.improvementPlans.empty')}</p>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('ctiDetail.improvementPlans.th.plan')}</TableHead>
          <TableHead>{t('ctiDetail.improvementPlans.th.owner')}</TableHead>
          <TableHead>{t('ctiDetail.improvementPlans.th.progress')}</TableHead>
          <TableHead>{t('ctiDetail.improvementPlans.th.deadline')}</TableHead>
          <TableHead>{t('ctiDetail.improvementPlans.th.status')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.map((p, i) => (
          <TableRow key={i}>
            <TableCell className="font-medium text-sm">{p.plan}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{p.owner}</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden w-20">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${p.progress}%` }} />
                </div>
                <span className="text-xs tabular-nums">{p.progress}%</span>
              </div>
            </TableCell>
            <TableCell className="text-xs">{p.deadline}</TableCell>
            <TableCell><Badge variant="outline" className="text-xs">{p.status}</Badge></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ---------- Intervention request form ----------
function InterventionForm({ data, onSubmitted }) {
  const { t } = useTranslation();
  const [urgency, setUrgency] = useState('medium');
  const [employee, setEmployee] = useState('');
  const [dept, setDept] = useState('');
  const [issue, setIssue] = useState('');
  const employees = data?.employees || [];
  const depts = data?.department_rollup || [];
  const submit = (e) => {
    e.preventDefault();
    if (!issue.trim()) return;
    const ticketId = `TFX-${Math.floor(Math.random() * 9000) + 1000}`;
    toast.success(t('ctiDetail.interventionRequest.success'), {
      description: `${t('ctiDetail.interventionRequest.ticketId')} ${ticketId}`,
    });
    onSubmitted?.();
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="cti-intervention-form">
      <div>
        <Label className="text-xs">{t('ctiDetail.interventionRequest.urgency')}</Label>
        <select value={urgency} onChange={(e) => setUrgency(e.target.value)}
                className="w-full mt-1 rounded-md border bg-background p-2 text-sm"
                data-testid="cti-intervention-urgency">
          <option value="low">{t('ctiDetail.risk.severity.low')}</option>
          <option value="medium">{t('ctiDetail.risk.severity.medium')}</option>
          <option value="high">{t('ctiDetail.risk.severity.high')}</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">{t('ctiDetail.interventionRequest.employee')}</Label>
        <select value={employee} onChange={(e) => setEmployee(e.target.value)}
                className="w-full mt-1 rounded-md border bg-background p-2 text-sm">
          <option value="">—</option>
          {employees.map((e, i) => <option key={i} value={e.user?.id || i}>{e.user?.name}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs">{t('ctiDetail.interventionRequest.department')}</Label>
        <select value={dept} onChange={(e) => setDept(e.target.value)}
                className="w-full mt-1 rounded-md border bg-background p-2 text-sm">
          <option value="">—</option>
          {depts.map((d, i) => <option key={i} value={d.department?.id}>{d.department?.name}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs">{t('ctiDetail.interventionRequest.issue')}</Label>
        <Textarea value={issue} onChange={(e) => setIssue(e.target.value)} rows={3} className="mt-1" data-testid="cti-intervention-issue" />
      </div>
      <Button type="submit" className="w-full" data-testid="cti-intervention-submit">
        <Wrench className="h-4 w-4 mr-1.5" /> {t('ctiDetail.interventionRequest.submit')}
      </Button>
    </form>
  );
}

// ---------- Recognition form ----------
function RecognitionForm({ data, onSubmitted }) {
  const { t } = useTranslation();
  const [employee, setEmployee] = useState('');
  const [badge, setBadge] = useState('topPerformer');
  const [message, setMessage] = useState('');
  const employees = data?.employees || [];
  const submit = (e) => {
    e.preventDefault();
    toast.success(t('ctiDetail.recognition.title'));
    onSubmitted?.();
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="cti-recognition-form">
      <div>
        <Label className="text-xs">{t('ctiDetail.recognition.employee')}</Label>
        <select value={employee} onChange={(e) => setEmployee(e.target.value)}
                className="w-full mt-1 rounded-md border bg-background p-2 text-sm">
          <option value="">—</option>
          {employees.map((e, i) => <option key={i} value={e.user?.id || i}>{e.user?.name}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs">{t('ctiDetail.recognition.badge')}</Label>
        <select value={badge} onChange={(e) => setBadge(e.target.value)}
                className="w-full mt-1 rounded-md border bg-background p-2 text-sm">
          {['topPerformer', 'teamPlayer', 'innovator', 'mentor', 'customerHero'].map((b) => (
            <option key={b} value={b}>{t(`ctiDetail.recognition.badges.${b}`)}</option>
          ))}
        </select>
      </div>
      <div>
        <Label className="text-xs">{t('ctiDetail.recognition.message')}</Label>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-1" />
      </div>
      <Button type="submit" className="w-full" data-testid="cti-recognition-submit">
        <Award className="h-4 w-4 mr-1.5" /> {t('ctiDetail.recognition.submit')}
      </Button>
    </form>
  );
}

// ---------- Bonus simulator ----------
function BonusSimulator({ data, currency }) {
  const { t } = useTranslation();
  const employees = data?.employees || [];
  const keys = ['kpi', 'performance', 'training', 'attendance', 'engagement', 'leadership', 'tenure', 'risk'];
  const [weights, setWeights] = useState({
    kpi: 25, performance: 25, training: 10, attendance: 10,
    engagement: 10, leadership: 10, tenure: 5, risk: 5,
  });
  const [budget, setBudget] = useState(100000);
  const total = keys.reduce((s, k) => s + weights[k], 0);
  const sumOk = total === 100;

  const rows = useMemo(() => employees.map((e) => {
    const perf = e.value?.value_score || 0;
    const score = Math.round(
      perf * (weights.kpi + weights.performance) / 100 +
      perf * 0.8 * weights.training / 100 +
      perf * 0.6 * weights.attendance / 100 +
      perf * 0.7 * weights.engagement / 100 +
      perf * 0.5 * weights.leadership / 100 +
      perf * 0.4 * weights.tenure / 100 -
      (100 - perf) * weights.risk / 100,
    );
    const eligible = score >= 60;
    return { name: e.user?.name, score, eligible, perf };
  }).sort((a, b) => b.score - a.score), [employees, weights]);

  const eligibleRows = rows.filter((r) => r.eligible);
  const totalScore = eligibleRows.reduce((s, r) => s + r.score, 0) || 1;
  const distributed = eligibleRows.reduce((s) => s + budget / eligibleRows.length, 0);
  const remaining = budget - distributed;

  const setWeight = (k, v) => setWeights({ ...weights, [k]: Math.round(v) });

  const onSave = () => toast.success(t('ctiDetail.bonus.saved'));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-3">
          <Label className="text-xs">{t('ctiDetail.bonus.budget')}</Label>
          <Input type="number" value={budget} onChange={(e) => setBudget(Number(e.target.value) || 0)}
                 className="mt-1" data-testid="cti-bonus-budget" />
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctiDetail.bonus.distribute')}</p>
          <p className="text-lg font-bold tabular-nums">{fmt(distributed, currency)}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctiDetail.bonus.remaining')}</p>
          <p className="text-lg font-bold tabular-nums">{fmt(remaining, currency)}</p>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-sm">{t('ctiDetail.bonus.weights')}</h4>
          <Badge className={sumOk ? 'bg-success/15 text-success border-success/30' : 'bg-destructive/15 text-destructive border-destructive/30'}
                 data-testid="cti-bonus-sum">
            {sumOk ? t('ctiDetail.bonus.sumOk') : t('ctiDetail.bonus.sumBad', { n: total })}
          </Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {keys.map((k) => (
            <div key={k} data-testid={`cti-bonus-weight-${k}`}>
              <div className="flex justify-between text-xs mb-1">
                <span>{t(`ctiDetail.bonus.${k}`)}</span>
                <span className="tabular-nums font-semibold">{weights[k]}%</span>
              </div>
              <Slider value={[weights[k]]} min={0} max={50} step={1}
                      onValueChange={(v) => setWeight(k, v[0])} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ctiDetail.bonus.table.collab')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.bonus.table.score')}</TableHead>
              <TableHead className="text-center">{t('ctiDetail.bonus.table.eligible')}</TableHead>
              <TableHead className="text-right">{t('ctiDetail.bonus.table.bonus')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 12).map((r, i) => {
              const bonus = r.eligible && eligibleRows.length > 0
                ? Math.round((r.score / totalScore) * budget)
                : 0;
              return (
                <TableRow key={i} data-testid={`cti-bonus-row-${i}`}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.score}</TableCell>
                  <TableCell className="text-center">
                    {r.eligible ? <Badge className="bg-success/15 text-success">{t('ctiDetail.bonus.eligibleYes')}</Badge> : <Badge variant="outline">{t('ctiDetail.bonus.eligibleNo')}</Badge>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmt(bonus, currency)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" data-testid="cti-bonus-export">{t('ctiDetail.bonus.export')}</Button>
        <Button size="sm" onClick={onSave} data-testid="cti-bonus-save"><Calculator className="h-4 w-4 mr-1.5" />{t('ctiDetail.bonus.save')}</Button>
      </div>
    </div>
  );
}

// ---------- Suite panel ----------
function SuitePanel({ suite }) {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <p className="text-sm">{t(`ctiDetail.suite.${suite}`)}</p>
    </Card>
  );
}

// ===========================================================================
// Main sheet
// ===========================================================================

const TITLE_KEYS = {
  roi: 'ctiDetail.roi.title',
  cost: 'ctiDetail.cost.title',
  value: 'ctiDetail.value.title',
  performance: 'ctiDetail.performance.title',
  risk: 'ctiDetail.risk.title',
  top: 'ctiDetail.top.title',
  dept: 'ctiDetail.dept.title',
  employee: 'ctiDetail.employee.title',
  pipeline: 'ctiDetail.pipeline.title',
  alerts: 'ctiDetail.alertsPanel.title',
  improvementPlans: 'ctiDetail.improvementPlans.title',
  intervention: 'ctiDetail.interventionRequest.title',
  recognition: 'ctiDetail.recognition.title',
  bonus: 'ctiDetail.bonus.title',
  suite: 'ctiDetail.suite.title',
};

export default function IntelligenceDetailSheet({
  open, onOpenChange, kind, data, currency = 'USD', payload = {},
}) {
  const { t } = useTranslation();
  const titleKey = TITLE_KEYS[kind] || 'ctiDetail.roi.title';
  const title = kind === 'suite'
    ? t(titleKey, { suite: payload.suite || '' })
    : t(titleKey);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid={`cti-sheet-${kind || 'none'}`}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {kind && (
            <SheetDescription>
              {t(`ctiDetail.${kind === 'top' ? 'top' : kind === 'dept' ? 'dept' : kind === 'alerts' ? 'alertsPanel' : kind === 'improvementPlans' ? 'improvementPlans' : kind === 'intervention' ? 'interventionRequest' : kind === 'employee' ? 'employee' : kind === 'pipeline' ? 'pipeline' : kind === 'recognition' ? 'recognition' : kind === 'bonus' ? 'bonus' : kind}.subtitle`, { defaultValue: '' })}
            </SheetDescription>
          )}
        </SheetHeader>
        <div className="mt-4 pb-8">
          {kind === 'roi' && <RoiPanel data={data} currency={currency} />}
          {kind === 'cost' && <CostPanel data={data} currency={currency} />}
          {kind === 'value' && <ValuePanel data={data} currency={currency} />}
          {kind === 'performance' && <PerformancePanel data={data} />}
          {kind === 'risk' && <RiskPanel data={data} />}
          {kind === 'top' && <TopPanel data={data} />}
          {kind === 'dept' && <DeptPanel data={data} dept={payload.dept} currency={currency} />}
          {kind === 'employee' && <EmployeePanel employee={payload.employee} currency={currency} />}
          {kind === 'pipeline' && <PipelinePanel data={data} level={payload.level} />}
          {kind === 'alerts' && <AlertsPanel data={data} />}
          {kind === 'improvementPlans' && <ImprovementPlansPanel data={data} />}
          {kind === 'intervention' && <InterventionForm data={data} onSubmitted={() => onOpenChange(false)} />}
          {kind === 'recognition' && <RecognitionForm data={data} onSubmitted={() => onOpenChange(false)} />}
          {kind === 'bonus' && <BonusSimulator data={data} currency={currency} />}
          {kind === 'suite' && <SuitePanel suite={payload.suite} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
